import { Button, Link as ChakraLink, Flex, Stack, Text, useBreakpointValue } from '@chakra-ui/react'
import { Global } from '@emotion/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Outlet } from 'react-router'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'

import ButtonNavigation from 'components/button-navigation'
import Freshchat from 'components/freshchat'
import MenuItem from 'components/menu-item'
import { useUserContext } from 'contexts/user-context'
import { isStreamingPassesActive } from 'utils/customer-utils'
import { isGraphicsFeatureEnabled } from 'utils/env-utils'

import { useGetCustomer, useGetCustomerYoutubeStatus } from 'api'
import theme from 'theme'

import PageHeader from './page-header'
import ClubMenu from './page-header/club-menu'

import type { NavigateOptions } from 'react-router-dom'

type MenuLayoutProps = {
  isMobile?: boolean
  onToggleMenu?: () => void
}

const Footer = ({ isMobile = false, onVersionClick }: { isMobile?: boolean; onVersionClick: () => void }) => {
  const textColour = isMobile ? theme.colors.white : theme.colors.gray[500]
  return (
    <Stack spacing={5}>
      <Stack spacing={2}>
        <ChakraLink
          as={RouterLink}
          isExternal
          to="https://www.playai.co"
        >
          <Button variant="link" size="xs" color={textColour} w="100%">
            <Flex justifyContent="space-between" gap={4} w="100%">
              PlayAI Terms of Service
              <FontAwesomeIcon icon={['far', 'external-link']} size="xs" />
            </Flex>
          </Button>
        </ChakraLink>
      </Stack>
      <Text
        w="100%"
        textStyle="caption"
        textAlign="center"
        color={{ base: theme.colors.white, lg: theme.baseColors.text.disabled }}
        py={4}
        userSelect="none"
        cursor="pointer"
        onClick={onVersionClick}
      >
        {`version ${import.meta.env.VITE_APP_VERSION}.${import.meta.env.VITE_CI_COMMIT_SHORT_SHA ?? 'dev'}`}
      </Text>
    </Stack>
  )
}

export const MenuLayout = ({ isMobile, onToggleMenu }: MenuLayoutProps) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { selectedCustomer, shouldNotifyNewVersion, signOut } = useUserContext()

  const { data: customer } = useGetCustomer(selectedCustomer?.customerGuid)
  const isCustomerStreamingPassesActive = isStreamingPassesActive(customer)

  const {
    data: { isConnected: isYoutubeConnected },
  } = useGetCustomerYoutubeStatus(selectedCustomer?.customerGuid)

  const queryClient = useQueryClient()
  const signOutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      queryClient.clear()
      window.localStorage.removeItem('fbx-customer')
      navigate('/login')
      window.location.reload()
    },
  })

  const navigateAndClose = (route: string, options?: NavigateOptions) => {
    navigate(route, options)
    if (onToggleMenu) {
      onToggleMenu()
    }
  }

  const currentCustomerGuid = window.localStorage.getItem('fbx-customer')

  return isMobile ? (
    <Stack h="100vh" justifyContent="space-between" alignItems="center">
      <Stack spacing={4} mt="80px" pb={2} w="100%" px={4} overflowY="scroll">
        <ClubMenu onSelect={onToggleMenu} />
        <ButtonNavigation type="transparent" text="Home" onClick={() => navigateAndClose('/')} />
        <ButtonNavigation
          type="transparent"
          text="Create Image"
          isDisabled={false}
          onClick={() => navigateAndClose('/image-generation')}
        />
        <ButtonNavigation
          type="transparent"
          text="Create Video"
          isDisabled={false}
          onClick={() => navigateAndClose('/create-video')}
        />
        <ButtonNavigation
          type="transparent"
          text="Manage Video"
          isDisabled={false}
          onClick={() => navigateAndClose('/manage-video')}
        />
        <ButtonNavigation
          type="transparent"
          text="Interactive Avatar"
          isDisabled={false}
          onClick={() => navigateAndClose('/interactive-avatar')}
        />
        <ButtonNavigation type="transparent" text="Account profile" onClick={() => navigateAndClose('/profile')} />
        <ButtonNavigation type="progress" text="Sign out" onClick={() => signOutMutation.mutateAsync()} />
      </Stack>
      <Footer isMobile onVersionClick={() => navigateAndClose('/whats-new')} />
    </Stack>
  ) : (
    <Stack h={`calc(100vh - ${HEADER_DESKTOP_HEIGHT}`} justifyContent="space-between">
      <Stack w={{ base: '236px', xl: '268px' }} spacing={4} my={4} mx={{ base: 0, xl: 2 }}>
        <MenuItem
          icon={['far', 'house']}
          text="Home"
          onClick={() => navigateAndClose('/')}
          isSelected={pathname === '/'}
        />
        <MenuItem
          icon={['far', 'image']}
          text="Create Image"
          onClick={() => navigateAndClose('/image-generation')}
          isSelected={pathname.indexOf('/image-generation') > -1}
          isDisabled={false}
        />
        <MenuItem
          icon={['far', 'play']}
          text="Create Video"
          onClick={() => navigateAndClose('/create-video')}
          isSelected={pathname.indexOf('/create-video') > -1}
          isDisabled={false}
        />
        <MenuItem
          icon={['far', 'video']}
          text="Manage Video"
          onClick={() => navigateAndClose('/manage-video')}
          isSelected={pathname.indexOf('/manage-video') > -1}
          isDisabled={false}
        />
        <MenuItem
          icon={['far', 'user']}
          text="Interactive Avatar"
          onClick={() => navigateAndClose('/interactive-avatar')}
          isSelected={pathname.indexOf('/interactive-avatar') > -1}
          isDisabled={false}
        />
      </Stack>
      <Footer onVersionClick={() => navigateAndClose('/')} />
    </Stack>
  )
}

export const HEADER_DESKTOP_HEIGHT = '64px'

const Layout = () => {
  const isSmallViewport = useBreakpointValue({ base: true, lg: false })

  return (
    <Flex w="100%" direction="column">
      {isSmallViewport ? (
        // Mobile
        <Flex w="100%" h={{ base: '116px', lg: HEADER_DESKTOP_HEIGHT }} align="center">
          <PageHeader />
        </Flex>
      ) : (
        // Desktop
        <Flex
          w="100%"
          h={{ base: '160px', lg: HEADER_DESKTOP_HEIGHT }}
          align="center"
          position="sticky"
          top="0"
          zIndex={1}
        >
          <PageHeader />
        </Flex>
      )}

      <Flex w="100%">
        {!isSmallViewport && (
          <Flex
            flex={1}
            justify="center"
            px="16px"
            h={`calc(100vh - ${HEADER_DESKTOP_HEIGHT})`}
            background={theme.baseColors.background.card}
            position="sticky"
            top={HEADER_DESKTOP_HEIGHT}
          >
            <MenuLayout />
          </Flex>
        )}
        <Flex
          w="100%"
          flex={{ base: 1, lg: 5 }}
          mt={{ base: '-32px', lg: '0px' }}
          p={{
            base: `0 ${theme.constants.viewportPadding}px`,
            lg: `${theme.constants.viewportPadding}px ${theme.constants.viewportPadding * 2}px`,
            xl: `${theme.constants.viewportPadding}px ${theme.constants.viewportPadding}px ${
              theme.constants.viewportPadding * 2
            }px`,
          }}
        >
          <Outlet />
        </Flex>
      </Flex>
      {/* Below is a hack to decrease the FreshChat widget's z-index to not be 2147483600, so it doesn't sit on top of modals */}
      <Global
        styles={`
          #fc_frame#fc_frame#fc_frame { z-index: 750 !important; bottom: 82px !important; right: 100px !important }
          #fc_frame#fc_frame#fc_frame.fc-widget-fullscreen.fc-widget-normal.h-open-container { bottom: 0px !important; right: 0px !important }
          #fc_frame#fc_frame#fc_frame.fc-mobile-view.h-open-container { bottom: 0px !important; right: 0px !important }
          #launcher-frame { z-index: 749 !important; }
        `}
      />
      <Freshchat />
    </Flex>
  )
}

export default Layout
